/**
 * Avatar Component - AWLP Design System
 * Circular avatar with initials fallback
 */

import styled, { css } from 'styled-components';

export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  firstName?: string;
  lastName?: string;
  imageUrl?: string;
  size?: AvatarSize;
  className?: string;
}

const getSizeStyles = (size: AvatarSize) => {
  const sizes = {
    sm: css`
      width: 32px;
      height: 32px;
      font-size: ${({ theme }) => theme.typography.fontSize.xs};
    `,
    md: css`
      width: 40px;
      height: 40px;
      font-size: ${({ theme }) => theme.typography.fontSize.sm};
    `,
    lg: css`
      width: 56px;
      height: 56px;
      font-size: ${({ theme }) => theme.typography.fontSize.base};
    `,
  };

  return sizes[size];
};

const Container = styled.div<{ $size: AvatarSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primary.base};
  color: ${({ theme }) => theme.colors.text.white};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  flex-shrink: 0;
  user-select: none;
  overflow: hidden;

  ${({ $size }) => getSizeStyles($size)}
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Initials = styled.span`
  text-transform: uppercase;
`;

const getInitials = (firstName?: string, lastName?: string): string => {
  if (!firstName && !lastName) return '?';

  const firstInitial = firstName?.charAt(0) || '';
  const lastInitial = lastName?.charAt(0) || '';

  return `${firstInitial}${lastInitial}`.toUpperCase();
};

export const Avatar = ({ firstName, lastName, imageUrl, size = 'md', className }: AvatarProps) => {
  return (
    <Container $size={size} className={className}>
      {imageUrl ? (
        <Image src={imageUrl} alt={`${firstName} ${lastName}`} />
      ) : (
        <Initials>{getInitials(firstName, lastName)}</Initials>
      )}
    </Container>
  );
};

export default Avatar;
